/* global Flickity */
/* eslint-disable  @wordpress/no-global-active-element */

/**
 * Close the modal when the escape key is pressed
 *
 * @param {Event}   event  The keydown event
 * @param {Element} modal  The current modal element
 * @param {Element} slider The whole slider element
 * @return {void}
 */
function handleEscapeKey(event, modal, slider) {
	if (event.key === 'Escape') {
		closeModal(modal, slider);
	}
}

/**
 * Close the modal when the button or overlay is clicked
 *
 * @param {Event}   event  The click event
 * @param {Element} modal  The current modal element
 * @param {Element} slider The whole slider element
 * @return {void}
 */
function handleModalClick(event, modal, slider) {
	event.preventDefault();
	closeModal(modal, slider);
}

// Keep track of the modal state
let isModalOpen = false;

/**
 * Creates a closure to handle the play button click event.
 *
 * @param {Element} slide  The slide element that contains the play button.
 * @param {Element} slider The whole slider element
 * @return {Function} The event handler function
 */
function createPlayButtonHandler(slide, slider) {
	return function handlePlayButton(event) {
		event.preventDefault();

		if (isModalOpen) {
			return;
		}
		isModalOpen = true;

		const url = slide.dataset.linkedMedia;
		if (!url) {
			return;
		}

		// create the modal dialog element
		const modal = document.createElement('modal');
		modal.classList.add('paragon-media-modal');

		// create the media wrapper element
		const media = document.createElement('div');
		media.classList.add('media');

		// create the iframe element that will contain the video
		const iframe = document.createElement('iframe');
		iframe.setAttribute('frameborder', '0');
		iframe.setAttribute('allow', 'autoplay; encrypted-media;');
		iframe.setAttribute('allowfullscreen', 'true');

		// set the iframe src and extra attributes based on the media type
		const mediaType = url.includes('youtu') ? 'youtube' : 'vimeo';
		const videoId = url.split('/').pop();
		if (mediaType === 'youtube') {
			iframe.setAttribute(
				'src',
				'https://www.youtube.com/embed/' + videoId + '?autoplay=1'
			);
		}
		if (mediaType === 'vimeo') {
			iframe.setAttribute(
				'src',
				'https://player.vimeo.com/video/' + videoId + '?autoplay=1'
			);
			iframe.setAttribute('width', '100%');
			iframe.setAttribute('height', '100%');
		}
		media.appendChild(iframe);

		// add the media element that contains the video to the modal
		modal.appendChild(media);

		// create a close button for the modal
		const closeButton = document.createElement('button');
		closeButton.classList.add('close-button');
		closeButton.setAttribute('aria-label', 'Close');
		closeButton.innerHTML =
			'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 165.81 165.81" height="48" width="48"><path d="m150.02,163.46L2.35,15.78c-3.13-3.13-3.13-8.2,0-11.32l2.12-2.12c3.12-3.12,8.19-3.12,11.31,0l147.68,147.68c3.13,3.13,3.13,8.2,0,11.32l-2.11,2.11c-3.13,3.13-8.2,3.13-11.32,0Z"/><path d="m4.46,163.46l-2.12-2.12c-3.13-3.13-3.13-8.2,0-11.32L150.02,2.35c3.13-3.13,8.2-3.13,11.32,0l2.12,2.12c3.12,3.12,3.12,8.19,0,11.31L15.78,163.46c-3.12,3.12-8.19,3.12-11.31,0Z"/></svg>';
		closeButton.onclick = function (closeButtonEvent) {
			handleModalClick(closeButtonEvent, modal, slider);
		};
		modal.appendChild(closeButton);

		// listen for tab key presses to contain focus within the modal
		document.addEventListener('keydown', containModalFocus, null, modal);

		// listen for clicks on the modal to close it
		modal.addEventListener(
			'click',
			function (modalClickEvent) {
				handleModalClick(modalClickEvent, modal, slider);
			},
			{ once: true }
		);

		// listen for the escape key to close the modal
		document.addEventListener('keydown', function (escapeKeyEvent) {
			handleEscapeKey(escapeKeyEvent, modal, slider);
		});

		// add the modal to the DOM
		document.body.appendChild(modal);

		const embed = modal.querySelector('iframe');
		embed.focus();
	};
}

/**
 * Closes the modal, removes event listeners and sets focus back to the slider.
 *
 * @param {Element} modal  The modal element to be removed.
 * @param {Element} slider The slider element to set focus on.
 *
 * @return {void}
 */
function closeModal(modal, slider) {
	modal.remove();
	isModalOpen = false;

	document.removeEventListener('keydown', handleEscapeKey);
	document.removeEventListener('keydown', containModalFocus);
	slider.focus();
}

/**
 * Contains the focus within the modal when it's open.
 *
 * @param {Event}   event The keydown event object.
 * @param {Element} modal The modal element to contain the focus within.
 *
 * @return {void}
 */
function containModalFocus(event, modal) {
	if (!modal) {
		return;
	}

	const focusableElements = modal.querySelectorAll(
		'iframe, button, [href], input, select, [tabindex]:not([tabindex="-1"])'
	);
	const firstFocusableElement = focusableElements[0];
	const lastFocusableElement =
		focusableElements[focusableElements.length - 1];
	if (event.key === 'Tab') {
		if (event.shiftKey) {
			if (document.activeElement === firstFocusableElement) {
				event.preventDefault();
				lastFocusableElement.focus();
			}
		} else if (document.activeElement === lastFocusableElement) {
			event.preventDefault();
			firstFocusableElement.focus();
		}
	}
}

/**
 * Sets the instructions for the current slide based on its content.
 *
 * This function checks if the current slide has a link or a play button and
 * updates the instructions accordingly.
 *
 * @param {number}   slideIndex   The index of the current slide.
 * @param {Element}  instructions The element containing the instructions.
 * @param {NodeList} slides       The list of all slides.
 * @return {void}
 */
function setInstructions(slideIndex, instructions, slides) {
	const activateInstructions = instructions.querySelector(
		'.instructions__activate'
	);
	const activateAction = instructions.querySelector(
		'.instructions__activate-action'
	);
	const currentSlide = slides[slideIndex];
	// does the current slide have a link or a play button?
	const hasLink = currentSlide.querySelector('a');
	const hasPlayButton = currentSlide.querySelector('.play-video-button');
	if (hasLink) {
		activateInstructions.style.display = 'block';
		activateAction.textContent = ' to follow link';
	}
	if (hasPlayButton) {
		activateInstructions.style.display = 'block';
		activateAction.textContent = ' to play video';
	}
	if (!hasLink && !hasPlayButton) {
		activateInstructions.style.display = 'none';
	}
}

/**
 * Initializes a Flickity instance for a given block.
 *
 * This function sets up a Flickity slider with various options and event listeners.
 * It also sets up keyboard navigation for the slider.
 *
 * @param {Element} block The block element that contains the slider and its controls.
 * @return {void}
 */
function initFlickityInstance(block) {
	const prefersReducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	);
	const autoplay = prefersReducedMotion.matches ? false : 5000;

	const slider = block.querySelector('.paragon-carousel-block__slider');
	const instructions = block.querySelector('.paragon-carousel-instructions');

	// create the Flickity instance
	new Flickity(slider, {
		cellAlign: 'left',
		contain: true,
		prevNextButtons: true,
		pageDots: false,
		wrapAround: true,
		autoPlay: autoplay,
		imagesLoaded: true,
		percentPosition: false,
		on: {
			change(index) {
				setInstructions(index, instructions, slides);
			},
		},
	});

	// we will hide the instructions when using the mouse
	slider.addEventListener('click', function () {
		slider.classList.add('mouse-interaction');
	});
	slider.addEventListener('keydown', function () {
		slider.classList.remove('mouse-interaction');
	});

	// make the slider focusable
	slider.setAttribute('tabindex', '0');

	// remove the tab index from the slider's children
	const tabbableElements = slider.querySelectorAll(
		'a, button, input, select, [tabindex]:not([tabindex="-1"])'
	);
	tabbableElements.forEach((element) => {
		element.setAttribute('tabindex', '-1');
	});

	// control the slider with the keyboard when it has focus
	const nextButton = slider.querySelector('.flickity-button.next');
	const prevButton = slider.querySelector('.flickity-button.previous');
	block.addEventListener('keydown', function (e) {
		// use arrow keys to navigate between slides
		if (e.key === 'ArrowLeft') {
			prevButton.classList.add('active');
			setTimeout(function () {
				prevButton.classList.remove('active');
			}, 200);
		} else if (e.key === 'ArrowRight') {
			nextButton.classList.add('active');
			setTimeout(function () {
				nextButton.classList.remove('active');
			}, 200);
		}
		// perform the default action for the slide when the enter key is pressed
		if (e.key === 'Enter') {
			e.preventDefault();
			const currentSlide = slider.querySelector('.is-selected');
			const playButton = currentSlide.querySelector('.play-video-button');
			if (playButton) {
				playButton.click();
			} else {
				const link = currentSlide.querySelector('a');
				if (link) {
					link.click();
				}
			}
		}
	});

	// set up the individual slides
	const slides = slider.querySelectorAll('.slide');
	slides.forEach((slide) => {
		// the play button is created in the PHP template
		const playButton = slide.querySelector('.play-video-button');

		// if the slide has a play button, add a click listener to the play button
		if (playButton) {
			const playButtonHandler = createPlayButtonHandler(slide, slider);
			playButton.addEventListener('click', playButtonHandler);
			slide.appendChild(playButton);
		}
		setInstructions(0, instructions, slides);
	});
}

/**
 * Initializes Flickity instances for all blocks on the page.
 *
 * @return {void}
 */
function carouselBlockInit() {
	const sliderBlocks = document.querySelectorAll(
		'[data-paragon="carousel-block"]'
	);

	// set up each slider as a new Flickity instance
	sliderBlocks.forEach((block) => {
		initFlickityInstance(block);
	});
}

document.addEventListener('DOMContentLoaded', carouselBlockInit);
